import {
  PDFDocument,
  rgb,
  degrees,
  BlendMode,
  pushGraphicsState,
  concatTransformationMatrix,
  popGraphicsState,
  degreesToRadians,
  scale,
  translate,
} from "pdf-lib-plus-encrypt";
import {
  aspectFitRect,
  hexToRgb,
  convertImageToMimeType,
  isValidDateFormat,
  downloadMultipleFiles,
  getImageDimensionsFromBlob,
} from "../Utils";
import WatermarkPositions from "../watermark_pdf/WatermarkPositions";
import NUpOutputOrientation from "../pdf_n_up_tool/NUpOutputOrientation";

//download pdf
export const downloadPdfDocument = async (
  title,
  width,
  height,
  user_password,
  images,
  callback,
) => {
  try {
    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();
    pdfDoc.setTitle(title);
    pdfDoc.setAuthor("shivapdf.com");
    pdfDoc.setSubject("Offline PDF Utilities");
    pdfDoc.setProducer("ShivaPDF");
    pdfDoc.setCreator("https://shivapdf.com");
    //loop
    for (var i = 0; i < images.length; i++) {
      // Add content to the PDF (e.g., a page and some text)
      const page = pdfDoc.addPage([width, height]);
      //add image
      const jpgImageBytes = await images[i].arrayBuffer();
      // Embed the JPG image bytes and PNG image bytes
      const jpgImage = await pdfDoc.embedJpg(jpgImageBytes);
      //dst rect
      const rect = aspectFitRect(
        [0, 0, width, height],
        [0, 0, jpgImage.width, jpgImage.height],
      );
      // Draw the JPG image in the center of the page
      page.drawImage(jpgImage, {
        x: rect[0],
        y: rect[1],
        width: rect[2],
        height: rect[3],
      });
    }

    //check if password set
    if (user_password !== "") {
      //user password to open pdf
      const userPassword = user_password;
      //password to modify pdf
      const ownerPassword = user_password;
      // Define passwords and permissions
      const permissions = {
        // Set what actions are disabled for the user (optional)
        noprint: true, // Disable printing
        nocopy: true, // Disable copying content
        noassemble: true, // Disable document assembly
      };

      // Encrypt the document
      // The encrypt method takes an object with userPassword, ownerPassword, and permissions
      await pdfDoc.encrypt({
        userPassword,
        ownerPassword,
        permissions,
      });
    }

    // Serialize the PDFDocument to bytes (Uint8Array)
    const pdfBytes = await pdfDoc.save();
    //return blob
    // 1. Create a Blob from the byte data
    // If your API returns a raw byte array, you can pass it directly: new Blob([pdfBytes], { type: 'application/pdf' })
    // If it's a Base64 string, you might need a helper function to convert it to a Uint8Array first.
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    //return
    callback(blob);
    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //return
    callback(null);
    //return
    return;
  }
};

//encrypt pdf
//using password and options
//options={printable:true, cancopy:true}
//pdfDocSrc is PDFDocument instance
export async function encryptPDF(
  title,
  pdfDoc,
  user_password,
  options,
  callback,
) {
  try {
    pdfDoc.setTitle(title);
    pdfDoc.setAuthor("shivapdf.com");
    pdfDoc.setSubject("Offline PDF Utilities");
    pdfDoc.setProducer("ShivaPDF");
    pdfDoc.setCreator("https://shivapdf.com");

    //check if password set
    if (user_password !== "") {
      //user password to open pdf
      const userPassword = user_password;
      //password to modify pdf
      const ownerPassword = user_password;
      // Define passwords and permissions
      const permissions = {
        // Set what actions are disabled for the user (optional)
        noprint: options.printable, // Disable printing
        nocopy: options.cancopy, // Disable copying content
        noassemble: true, // Disable document assembly
      };

      // Encrypt the document
      // The encrypt method takes an object with userPassword, ownerPassword, and permissions
      await pdfDoc.encrypt({
        userPassword,
        ownerPassword,
        permissions,
      });
    }
    // Serialize the PDFDocument to bytes (Uint8Array)
    const pdfBytes = await pdfDoc.save();
    //return blob
    // 1. Create a Blob from the byte data
    // If your API returns a raw byte array, you can pass it directly: new Blob([pdfBytes], { type: 'application/pdf' })
    // If it's a Base64 string, you might need a helper function to convert it to a Uint8Array first.
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    //return
    callback(blob);
    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //return
    callback(null);
    //return
    return;
  }
}

//watermark pdf
//add watermark text to pdf
//pdfDocSrc is PDFDocument instance
export async function watermarkPDFDocument(
  title,
  pdfDoc,
  user_password,
  configurations,
  callback,
) {
  try {
    pdfDoc.setTitle(title);
    pdfDoc.setAuthor("shivapdf.com");
    pdfDoc.setSubject("Offline PDF Utilities");
    pdfDoc.setProducer("ShivaPDF");
    pdfDoc.setCreator("https://shivapdf.com");

    //loop pages
    const pages = pdfDoc.getPages();

    console.log("Settings: ", configurations);
    //loop
    for (var i = 0; i < pages.length; i++) {
      //page
      const page = pages[i];

      //draw text
      //if set
      if (configurations.text !== "") {
        //add font
        // Embed a standard font
        const font = await pdfDoc.embedFont(configurations.font);
        //colo r,g,b
        const color = hexToRgb(configurations.color);

        //text width
        const textWidth = font.widthOfTextAtSize(
          configurations.text,
          configurations.font_size,
        );
        //text height
        const textHeight = font.heightAtSize(configurations.font_size);
        //position
        const position = getPositionCoordinates(
          configurations.position,
          page.getSize(),
          textWidth,
          textHeight,
          configurations.padding,
        );

        //shift origin to text center
        const originX = position[0] + textWidth * 0.5;
        const originY = position[1] + textHeight * 0.5;

        // Use low-level operators to manipulate the coordinate system
        page.pushOperators(
          pushGraphicsState(),
          // 1. Translate to origin point
          concatTransformationMatrix(1, 0, 0, 1, originX, originY),
          // 2. Rotate
          concatTransformationMatrix(
            Math.cos(degreesToRadians(configurations.rotation)),
            Math.sin(degreesToRadians(configurations.rotation)),
            -Math.sin(degreesToRadians(configurations.rotation)),
            Math.cos(degreesToRadians(configurations.rotation)),
            0,
            0,
          ),
          // 3. Translate back
          concatTransformationMatrix(1, 0, 0, 1, -originX, -originY),
        );

        // Draw rotated text
        //as context already translatred and rotated
        //draw text at 0, 0 and rotation 0
        page.drawText(String(configurations.text), {
          x: position[0],
          y: position[1],
          size: Number(configurations.font_size),
          font: font,
          color: rgb(color.r, color.g, color.b),
          rotate: degrees(0),
          opacity: configurations.opacity / 100,
          blendMode:
            configurations.on_top === true
              ? BlendMode.Normal
              : BlendMode.Normal, // Example blend mode
        });

        // Restore the graphics state
        page.pushOperators(popGraphicsState());
      }

      //draw image
      //if not null
      if (configurations.image !== null) {
        //convert to jpeg
        const image = await convertImageToMimeType(
          configurations.image,
          "image/jpeg",
          1,
        );
        //add image
        const jpgImageBytes = await image.arrayBuffer();
        // Embed the JPG image bytes and PNG image bytes
        const jpgImage = await pdfDoc.embedJpg(jpgImageBytes);
        //image width
        const imageWidth = jpgImage.width;
        //image height
        const imageHeight = jpgImage.height;
        //position
        const position = getPositionCoordinates(
          configurations.position,
          page.getSize(),
          imageWidth,
          imageHeight,
          configurations.padding,
        );

        //shift origin to text center
        const originX = position[0] + imageWidth * 0.5;
        const originY = position[1] + imageHeight * 0.5;

        // Use low-level operators to manipulate the coordinate system
        page.pushOperators(
          pushGraphicsState(),
          // 1. Translate to origin point
          concatTransformationMatrix(1, 0, 0, 1, originX, originY),
          // 2. Rotate
          concatTransformationMatrix(
            Math.cos(degreesToRadians(configurations.rotation)),
            Math.sin(degreesToRadians(configurations.rotation)),
            -Math.sin(degreesToRadians(configurations.rotation)),
            Math.cos(degreesToRadians(configurations.rotation)),
            0,
            0,
          ),
          // 3. Translate back
          concatTransformationMatrix(1, 0, 0, 1, -originX, -originY),
        );
        // Draw the JPG image in the center of the page
        page.drawImage(jpgImage, {
          x: position[0],
          y: position[1],
          opacity: configurations.opacity / 100,
          width: jpgImage.width,
          height: jpgImage.height,
          rotate: degrees(0),
        });

        // Restore the graphics state
        page.pushOperators(popGraphicsState());
      }
    }

    //check if password set
    if (user_password !== "") {
      //user password to open pdf
      const userPassword = user_password;
      //password to modify pdf
      const ownerPassword = user_password;
      // Define passwords and permissions
      const permissions = {
        // Set what actions are disabled for the user (optional)
        noprint: true, // Disable printing
        nocopy: true, // Disable copying content
        noassemble: true, // Disable document assembly
      };

      // Encrypt the document
      // The encrypt method takes an object with userPassword, ownerPassword, and permissions
      await pdfDoc.encrypt({
        userPassword,
        ownerPassword,
        permissions,
      });
    }
    // Serialize the PDFDocument to bytes (Uint8Array)
    const pdfBytes = await pdfDoc.save();
    //return blob
    // 1. Create a Blob from the byte data
    // If your API returns a raw byte array, you can pass it directly: new Blob([pdfBytes], { type: 'application/pdf' })
    // If it's a Base64 string, you might need a helper function to convert it to a Uint8Array first.
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    //return
    callback(blob);
    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //return
    callback(null);
    //return
    return;
  }
}

//get position coordinate from location
export function getPositionCoordinates(
  watermark_position,
  pageSize, // as a object {width:, height:}
  textWidth,
  textHeight,
  padding, //{x:, y:}
) {
  //page size
  const { width, height } = pageSize;
  //switch
  switch (watermark_position) {
    case String(WatermarkPositions.Center):
      //x,y
      const x1 = (width - textWidth) * 0.5;
      const y1 = (height - textHeight) * 0.5;
      return [x1 + padding.x, y1 + padding.y];

    case String(WatermarkPositions.Top_Left):
      //x,y
      const x2 = 0;
      const y2 = height - textHeight;
      return [x2 + padding.x, y2 + padding.y];

    case String(WatermarkPositions.Top):
      //x,y
      const x3 = (width - textWidth) * 0.5;
      const y3 = height - textHeight;
      return [x3 + padding.x, y3 + padding.y];

    case String(WatermarkPositions.Top_Right):
      //x,y
      const x4 = width - textWidth;
      const y4 = height - textHeight;
      return [x4 + padding.x, y4 + padding.y];

    case String(WatermarkPositions.Right):
      //x,y
      const x5 = width - textWidth;
      const y5 = (height - textHeight) * 0.5;
      return [x5 + padding.x, y5 + padding.y];

    case String(WatermarkPositions.Bottom_Right):
      //x,y
      const x6 = width - textWidth;
      const y6 = 0;
      return [x6 + padding.x, y6 + padding.y];

    case String(WatermarkPositions.Bottom):
      //x,y
      const x7 = (width - textWidth) * 0.5;
      const y7 = 0;
      return [x7 + padding.x, y7 + padding.y];

    case String(WatermarkPositions.Bottom_Left):
      //x,y
      const x8 = 0;
      const y8 = 0;
      return [x8 + padding.x, y8 + padding.y];

    case String(WatermarkPositions.Left):
      //x,y
      const x9 = 0;
      const y9 = (height - textHeight) * 0.5;
      return [x9 + padding.x, y9 + padding.y];

    default: //center
      //x,y
      const x0 = (width - textWidth) * 0.5;
      const y0 = (height - textHeight) * 0.5;
      return [x0 + padding.x, y0 + padding.y];
  }
}

//resize pdf
//resize pdf pages
//pdfdoc = PDFDocument instance
export async function resizePDFDocument(
  title,
  pdfDoc,
  dimension,
  password,
  progress,
  callback,
) {
  try {
    // Define new dimensions (e.g., Letter size in PDF units: 612x792 points)
    const newWidth = dimension[0];
    const newHeight = dimension[1];

    //destination pdf
    const dstPdf = await PDFDocument.create();

    // Embed the desired page from the source document into the destination document
    // In this example, we embed the first page (index 0) of the source PDF.
    const embeddedPages = await dstPdf.embedPdf(
      pdfDoc,
      pdfDoc.getPageIndices(),
    ); // The second arg is an array of page indices

    embeddedPages.forEach((embeddedPage, i) => {
      //add page
      const page = dstPdf.addPage([newWidth, newHeight]);
      // Calculate scaling factors for the content
      const scaleX = newWidth / embeddedPage.width;
      const scaleY = newHeight / embeddedPage.height;
      const scaleContent = Math.min(scaleX, scaleY);

      // Center the content on the new page (optional)
      const scaledDiffWidth =
        (newWidth - scaleContent * embeddedPage.width) * 0.5;
      const scaledDiffHeight =
        (newHeight - scaleContent * embeddedPage.height) * 0.5;

      //Draw the embedded page onto the destination page
      page.drawPage(embeddedPage, {
        x: scaledDiffWidth,
        y: scaledDiffHeight,
        width: embeddedPage.width * scaleContent, // Optional: scale the page down
        height: embeddedPage.height * scaleContent,
      });

      //callback
      progress(i / embeddedPages.length);
    });

    //encrypt and save pdf
    await encryptPDF(
      title,
      dstPdf,
      password,
      {
        printable: true,
        cancopy: true,
      },
      callback,
    );
  } catch (e) {
    console.log("Error: ", e);
    //callback
    callback(null);
  }
}

//change pdf metadata
//pdfdoc PDFDocument instance
export async function changePDFMetaData(
  pdfDoc,
  metadataSettings,
  user_password,
  callback,
) {
  try {
    //set metadata
    pdfDoc.setTitle(
      metadataSettings.title === ""
        ? (pdfDoc.getTitle() ?? "")
        : metadataSettings.title,
    );
    pdfDoc.setAuthor(
      metadataSettings.author === ""
        ? (pdfDoc.getAuthor() ?? "")
        : metadataSettings.author,
    );
    pdfDoc.setSubject(
      metadataSettings.subject === ""
        ? (pdfDoc.getSubject() ?? "")
        : metadataSettings.subject,
    );
    pdfDoc.setProducer(
      metadataSettings.producer === ""
        ? (pdfDoc.getProducer() ?? "")
        : metadataSettings.producer,
    );
    pdfDoc.setCreator(
      metadataSettings.creator === ""
        ? (pdfDoc.getCreator() ?? "")
        : metadataSettings.creator,
    );
    pdfDoc.setCreationDate(
      isValidDateFormat(metadataSettings.create_date)
        ? new Date(metadataSettings.create_date)
        : (pdfDoc.getCreationDate() ?? new Date()),
    );
    pdfDoc.setModificationDate(
      isValidDateFormat(metadataSettings.mod_date)
        ? new Date(metadataSettings.mod_date)
        : (pdfDoc.getModificationDate() ?? new Date()),
    );
    pdfDoc.setKeywords(
      metadataSettings.keywords.split(",").length === 0
        ? (pdfDoc.getKeywords() ?? [])
        : metadataSettings.keywords.split(","),
    );
    //check if password set
    if (user_password !== "") {
      //user password to open pdf
      const userPassword = user_password;
      //password to modify pdf
      const ownerPassword = user_password;
      // Define passwords and permissions
      const permissions = {
        // Set what actions are disabled for the user (optional)
        noprint: true, // Disable printing
        nocopy: true, // Disable copying content
        noassemble: true, // Disable document assembly
      };

      // Encrypt the document
      // The encrypt method takes an object with userPassword, ownerPassword, and permissions
      await pdfDoc.encrypt({
        userPassword,
        ownerPassword,
        permissions,
      });
    }
    // Serialize the PDFDocument to bytes (Uint8Array)
    const pdfBytes = await pdfDoc.save();
    //return blob
    // 1. Create a Blob from the byte data
    // If your API returns a raw byte array, you can pass it directly: new Blob([pdfBytes], { type: 'application/pdf' })
    // If it's a Base64 string, you might need a helper function to convert it to a Uint8Array first.
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    //return
    callback(blob);
    //return
    return;
  } catch {
    //return
    callback(null);
    //return
    return;
  }
}

//flatten pdf file
//flatten form fields
//pdfdoc is PDFDocument instance
export async function flattenFormFields(
  title,
  pdfDoc,
  user_password,
  callback,
) {
  try {
    pdfDoc.setTitle(title);
    pdfDoc.setAuthor("shivapdf.com");
    pdfDoc.setSubject("Offline PDF Utilities");
    pdfDoc.setProducer("ShivaPDF");
    pdfDoc.setCreator("https://shivapdf.com");

    //get form
    const form = pdfDoc.getForm();
    // Flatten the form's fields
    form.flatten();

    //check if password set
    if (user_password !== "") {
      //user password to open pdf
      const userPassword = user_password;
      //password to modify pdf
      const ownerPassword = user_password;
      // Define passwords and permissions
      const permissions = {
        // Set what actions are disabled for the user (optional)
        noprint: true, // Disable printing
        nocopy: true, // Disable copying content
        noassemble: true, // Disable document assembly
      };

      // Encrypt the document
      // The encrypt method takes an object with userPassword, ownerPassword, and permissions
      await pdfDoc.encrypt({
        userPassword,
        ownerPassword,
        permissions,
      });
    }
    // Serialize the PDFDocument to bytes (Uint8Array)
    const pdfBytes = await pdfDoc.save();
    //return blob
    // 1. Create a Blob from the byte data
    // If your API returns a raw byte array, you can pass it directly: new Blob([pdfBytes], { type: 'application/pdf' })
    // If it's a Base64 string, you might need a helper function to convert it to a Uint8Array first.
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    //return
    callback(blob);
    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //return
    callback(null);
    //return
    return;
  }
}

//extract pdf pages as seperate pdfs
//pdfdoc is PDFDocument instance
export async function zipPDFPagesAsSeperatePDFs(title, pdfDoc, callback) {
  //try-catch
  try {
    //pdf seperate pages blob array
    var pdfPagesBlob = [];

    // Get all page indices from the source document
    const pageIndices = pdfDoc.getPageIndices();

    //indert to new pdf
    for (let i = 0; i < pageIndices.length; i++) {
      //new pdf
      const newPdfDoc = await PDFDocument.create();
      newPdfDoc.setTitle(title);
      newPdfDoc.setAuthor("shivapdf.com");
      newPdfDoc.setSubject("Offline PDF Utilities");
      newPdfDoc.setProducer("ShivaPDF");
      newPdfDoc.setCreator("https://shivapdf.com");

      //copy pages
      const [copiedPage] = await newPdfDoc.copyPages(pdfDoc, [pageIndices[i]]);

      //add page
      newPdfDoc.addPage(copiedPage);

      //Save the new, merged document
      const pdfBytes = await newPdfDoc.save();
      // 1. Create a Blob from the byte data
      // If your API returns a raw byte array, you can pass it directly: new Blob([pdfBytes], { type: 'application/pdf' })
      // If it's a Base64 string, you might need a helper function to convert it to a Uint8Array first.
      const blob = new Blob([pdfBytes], {
        type: "application/pdf",
      });

      //add
      pdfPagesBlob.push(blob);
    }

    //zip blobs
    const zipBlob = await downloadMultipleFiles(
      pdfPagesBlob,
      "extracted_pages",
      "STORE",
    );

    //callback
    callback(zipBlob);
    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //callback
    callback(null);
    //return
    return;
  }
}

//rotate and rearrange pdf documents
//pdfdoc instance of PDFDocument
export async function rotateRearrangePDFDocument(
  title,
  pdfDoc,
  pages, //pdf pages object {index, page_no, rotate, thumbnail}
  user_password,
  progress,
  callback,
) {
  //try-catch
  try {
    //new pdf
    const newPdfDoc = await PDFDocument.create();
    newPdfDoc.setTitle(title);
    newPdfDoc.setAuthor("shivapdf.com");
    newPdfDoc.setSubject("Offline PDF Utilities");
    newPdfDoc.setProducer("ShivaPDF");
    newPdfDoc.setCreator("https://shivapdf.com");

    //loop
    for (var i = 0; i < pages.length; i++) {
      //page info
      const pageInfo = pages[i];

      //copy all pages
      const [embeddedPage] = await newPdfDoc.embedPdf(pdfDoc, [
        Number(pageInfo.index),
      ]);

      //add page
      const page = newPdfDoc.addPage([embeddedPage.width, embeddedPage.height]);

      //save ggraphics state
      page.pushOperators(
        pushGraphicsState(),
        scale(pageInfo.flipH ? -1 : 1, pageInfo.flipV ? -1 : 1), // Flips the y-axis
        translate(
          pageInfo.flipH ? -embeddedPage.width : 0,
          pageInfo.flipV ? -embeddedPage.height : 0,
        ), // Translates the origin back into view
      );

      //draw page at x,y = 0,0
      page.drawPage(embeddedPage, {
        x: 0,
        y: 0,
      });

      // Restore previous graphics state
      page.pushOperators(popGraphicsState());

      // Get current rotation and add the new angle
      const currentRotation = page.getRotation().angle;
      const newAngle = (currentRotation + pageInfo.rotate) % 360;

      // Set the new rotation
      page.setRotation(degrees(newAngle));

      //progress callback
      progress(i / pages.length);
    }

    //encrypt pdf
    await encryptPDF(
      title,
      newPdfDoc,
      user_password,
      {
        printable: true,
        cancopy: true,
      },
      callback,
    );

    //return
    return;
  } catch {
    //callback
    callback(null);
    //return
    return;
  }
}

//add blank pdf page
//pdfdoc instance of PDFDocument
//dimension = new page width, height
export async function addBlankPDFPagesToPDF(
  title,
  pdfDoc,
  pages, //pdf pages object {index, page_no, thumbnail} index = -1 for blank page
  dimension, // [width, height]
  user_password,
  progress,
  callback,
) {
  //try-catch
  try {
    //new pdf
    const newPdfDoc = await PDFDocument.create();
    newPdfDoc.setTitle(title);
    newPdfDoc.setAuthor("shivapdf.com");
    newPdfDoc.setSubject("Offline PDF Utilities");
    newPdfDoc.setProducer("ShivaPDF");
    newPdfDoc.setCreator("https://shivapdf.com");

    //loop
    for (var i = 0; i < pages.length; i++) {
      //page info
      const pageInfo = pages[i];

      //check if blank page
      if (pageInfo.index === -1) {
        //add blank page
        newPdfDoc.addPage(dimension);
      } else {
        //copy all pages
        const [copiedPage] = await newPdfDoc.copyPages(pdfDoc, [
          Number(pageInfo.index),
        ]);
        //add page
        newPdfDoc.addPage(copiedPage);
      }

      //progress callback
      progress(i / pages.length);
    }

    //encrypt pdf
    await encryptPDF(
      title,
      newPdfDoc,
      user_password,
      {
        printable: true,
        cancopy: true,
      },
      callback,
    );

    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //callback
    callback(null);
    //return
    return;
  }
}

//add page numbrer to pdf
//pdfdoc instance of PDFDocument
export async function addPageNumbersToPDF(
  title,
  pdfDoc,
  user_password,
  configurations,
  progress,
  callback,
) {
  try {
    pdfDoc.setTitle(title);
    pdfDoc.setAuthor("shivapdf.com");
    pdfDoc.setSubject("Offline PDF Utilities");
    pdfDoc.setProducer("ShivaPDF");
    pdfDoc.setCreator("https://shivapdf.com");

    //loop pages
    const pages = pdfDoc.getPages();

    console.log("Settings: ", configurations);
    //loop
    for (var i = 0; i < pages.length; i++) {
      //page
      const page = pages[i];

      //text
      const page_number = `${i + (configurations.start_from_zero ? 0 : 1)}`;

      //add font
      // Embed a standard font
      const font = await pdfDoc.embedFont(configurations.font);
      //colo r,g,b
      const color = hexToRgb(configurations.color);

      //text width
      const textWidth = font.widthOfTextAtSize(
        page_number,
        configurations.font_size,
      );
      //text height
      const textHeight = font.heightAtSize(configurations.font_size);
      //position
      const position = getPositionCoordinates(
        configurations.position,
        page.getSize(),
        textWidth,
        textHeight,
        configurations.padding,
      );

      //shift origin to text center
      const originX = position[0] + textWidth * 0.5;
      const originY = position[1] + textHeight * 0.5;

      // Use low-level operators to manipulate the coordinate system
      page.pushOperators(
        pushGraphicsState(),
        // 1. Translate to origin point
        concatTransformationMatrix(1, 0, 0, 1, originX, originY),
        // 2. Rotate
        concatTransformationMatrix(
          Math.cos(degreesToRadians(configurations.rotation)),
          Math.sin(degreesToRadians(configurations.rotation)),
          -Math.sin(degreesToRadians(configurations.rotation)),
          Math.cos(degreesToRadians(configurations.rotation)),
          0,
          0,
        ),
        // 3. Translate back
        concatTransformationMatrix(1, 0, 0, 1, -originX, -originY),
      );

      // Draw rotated text
      //as context already translatred and rotated
      //draw text at 0, 0 and rotation 0
      page.drawText(String(page_number), {
        x: position[0],
        y: position[1],
        size: Number(configurations.font_size),
        font: font,
        color: rgb(color.r, color.g, color.b),
        opacity: configurations.opacity / 100,
      });

      // Restore the graphics state
      page.pushOperators(popGraphicsState());

      //progress
      progress(i / pages.length);
    }

    //encrypt pdf
    await encryptPDF(
      title,
      pdfDoc,
      user_password,
      {
        printable: true,
        cancopy: true,
      },
      callback,
    );

    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //return
    callback(null);
    //return
    return;
  }
}

//n up arrange pages
//pdfdoc instance of PDFDocument
//configurations = {rows: , cols: , orientation:}
export async function nUPToolArrangePages(
  title,
  pdfDoc,
  user_password,
  configurations,
  progress,
  callback,
) {
  //try - catch
  try {
    //destination pdf
    const dstPdf = await PDFDocument.create();
    //set pdf metadata
    dstPdf.setTitle(title);
    dstPdf.setAuthor("shivapdf.com");
    dstPdf.setSubject("Offline PDF Utilities");
    dstPdf.setProducer("ShivaPDF");
    dstPdf.setCreator("https://shivapdf.com");

    //output page size a4 8.27 * 11.69 in
    //we will be generating page in 300 dpi
    //page width
    const output_width =
      configurations.orientation === NUpOutputOrientation.LANDSCAPE
        ? 3508
        : 2480;
    //page height
    const output_height =
      configurations.orientation === NUpOutputOrientation.LANDSCAPE
        ? 2480
        : 3508;

    //all page indoices
    const indices = pdfDoc.getPageIndices();

    console.log("Settings: ", configurations);
    //current page index
    //drawing
    var pageIndexDrawing = 0;
    //total_pages / pages per sheet
    const pagesCount = Math.ceil(
      indices.length / (configurations.rows * configurations.cols),
    );

    //loop
    for (var p = 0; p < pagesCount; p++) {
      //add new page
      const page = dstPdf.addPage([output_width, output_height]);
      //loop
      //rows
      for (var i = 0; i < configurations.rows; i++) {
        //cols
        for (var j = 0; j < configurations.cols; j++) {
          //x origin
          const x = j * (output_width / configurations.cols);
          //y origin
          const y =
            output_height -
            output_height / configurations.rows -
            i * (output_height / configurations.rows);
          //width
          const width = output_width / configurations.cols;
          //height
          const height = output_height / configurations.rows;
          //check
          if (pageIndexDrawing < indices.length) {
            // Embed the desired page from the source document into the destination document
            // In this example, we embed the first page (index 0) of the source PDF.
            const [embeddedPage] = await dstPdf.embedPdf(pdfDoc, [
              indices[pageIndexDrawing],
            ]); // The second arg is an array of page indices

            //embedded page size
            const embeddedPageSize = embeddedPage.size();

            //scale required to fill the rect
            const scaleX = width / embeddedPageSize.width;
            const scaleY = height / embeddedPageSize.height;
            //check which one is larger for aspect fit
            const finalScale = scaleX < scaleY ? scaleX : scaleY;
            //final page width
            const finalWidth = embeddedPageSize.width * finalScale;
            //final page height
            const finalHeight = embeddedPageSize.height * finalScale;

            //draw in pages
            page.drawPage(embeddedPage, {
              x: x + (width - finalWidth) * 0.5,
              y: y + (height - finalHeight) * 0.5,
              width: finalWidth,
              height: finalHeight,
            });

            //increment
            pageIndexDrawing = pageIndexDrawing + 1;

            //progress
            progress(pageIndexDrawing / indices.length);
          }
        }
      }
    }

    //encrypy pdf
    await encryptPDF(
      title,
      dstPdf,
      user_password,
      {
        printable: true,
        cancopy: true,
      },
      callback,
    );
    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //callback
    callback(null);
    //return
    return;
  }
}

//html to pdf
//configurations = {password: , filename:, width:, height:, }
//brak image according to page size
export async function htmlImageToPDF(title, image, configurations, callback) {
  //image dimension
  const imageDimensions = await getImageDimensionsFromBlob(image);

  //fill image width to pdf width
  const pageWidth = Number(configurations.width);
  const pageHeight = Number(configurations.height);

  //try-catch
  try {
    //scale width
    //if imagewidth > pagewidth scale width
    //else dont scale
    const imageScale =
      imageDimensions.width > pageWidth ? pageWidth / imageDimensions.width : 1;

    //destination pdf
    const pdfDoc = await PDFDocument.create();
    //set pdf metadata
    pdfDoc.setTitle(title);
    pdfDoc.setAuthor("shivapdf.com");
    pdfDoc.setSubject("Offline PDF Utilities");
    pdfDoc.setProducer("ShivaPDF");
    pdfDoc.setCreator("https://shivapdf.com");

    //add image
    const pngImageBytes = await image.arrayBuffer();
    // Embed the PNG image bytes and PNG image bytes
    const pnpImage = await pdfDoc.embedPng(pngImageBytes);
    //scale image
    // Get the width/height of the JPG image scaled down to x of its original size
    const pngDims = pnpImage.scale(imageScale);

    //number of pdf pages required to accommodate image
    const pageCount = Math.ceil(pngDims.height / pageHeight);
    //loop
    for (let i = 0; i < pageCount; i++) {
      // Add content to the PDF (e.g., a page and some text)
      const page = pdfDoc.addPage([pageWidth, pageHeight]);

      //x,y,width,height
      const x = (pageWidth - pngDims.width) * 0.5;
      const y =
        pageHeight -
        (i === pageCount - 1
          ? pngDims.height - i * pageHeight
          : (pngDims.height / pageCount) * (pageCount - i));
      const width = pngDims.width;
      const height = pngDims.height;

      // Draw the JPG image in the center of the page
      page.drawImage(pnpImage, {
        x: x,
        y: y,
        width: width,
        height: height,
      });
    }

    //encrypt pdf
    await encryptPDF(
      title,
      pdfDoc,
      configurations.password,
      {
        printable: true,
        cancopy: true,
      },
      callback,
    );

    //return
    return;
  } catch (e) {
    console.log("Error: ", e);
    //callback
    callback(null);
    //return
    return;
  }
}

//crop pdf
//pdfdoc is PDFDocument instance
//configurations = crop rect in percentage {x:, y:, width:, height:, selectedPages:, password}
export async function cropPDF(title, pdfDoc, configurations, callback) {
  console.log("Configurations: ", configurations);
  //try catch
  try {
    //set pdf metadata
    pdfDoc.setTitle(title);
    pdfDoc.setAuthor("shivapdf.com");
    pdfDoc.setSubject("Offline PDF Utilities");
    pdfDoc.setProducer("ShivaPDF");
    pdfDoc.setCreator("https://shivapdf.com");

    // Get the all pages
    const pages = pdfDoc.getPages();
    //loop
    for (let i = 0; i < pages.length; i++) {
      //set crop box
      if (configurations.selectedPages.includes(Number(i)) === true) {
        //get page
        const page = pages[i];
        // Get the current dimensions
        const { width, height } = page.getSize();

        //set box
        page.setCropBox(
          (configurations.x * width) / 100,
          height -
            (configurations.y * height + configurations.height * height) / 100,
          (configurations.width * width) / 100,
          (configurations.height * height) / 100,
        );
      }
    }

    //encrypt pdf
    await encryptPDF(
      title,
      pdfDoc,
      configurations.password,
      {
        printable: true,
        cancopy: true,
      },
      callback,
    );

    //return
    return;
  } catch {
    //callback
    callback(null);
    //return
    return;
  }
}

//repair and extract pdf pages
//pdfdoc is PDFDocument instance
export async function repairPDFDoc(title, pdfDoc, user_password, callback) {
  //try - catch
  try {
    //set pdf metadata
    pdfDoc.setTitle(title);
    pdfDoc.setAuthor("shivapdf.com");
    pdfDoc.setSubject("Offline PDF Utilities");
    pdfDoc.setProducer("ShivaPDF");
    pdfDoc.setCreator("https://shivapdf.com");

    //encrypt pdf
    //encrypt pdf
    await encryptPDF(
      title,
      pdfDoc,
      user_password,
      {
        printable: true,
        cancopy: true,
      },
      callback,
    );

    //return
    return;
  } catch {
    //callback
    callback(null);
    //return
    return;
  }
}
